import { RefreshCcw, Trash2 } from "lucide-react";
import { Button } from "../button";
import { Spinner } from "../spinner";
import { Text } from "../typography";
import { createClickHandler, formatFileSize, getFileIcon, getFileNameWithoutExtension } from "./helpers";
import { FileActionsProps, FileInfoProps, FileItemRowProps, FileListProps } from "./types";

const FileInfo = ({ fileName, fileSize, status }: FileInfoProps) => {
    const getStatusText = () => {
        if (status === "upload_error") {
            return (
                <Text size="tiny" className="setu:mt-1 setu:text-destructive setu:text-left">
                    Error uploading this file. Try uploading the file again.
                </Text>
            );
        }

        if (status === "remove_error") {
            return (
                <Text size="tiny" className="setu:mt-1 setu:text-destructive setu:text-left">
                    Error removing this file. Try removing the file again.
                </Text>
            );
        }

        if (status === "uploading") {
            return (
                <Text size="tiny" isMuted className="setu:mt-1 setu:text-left">
                    uploading...
                </Text>
            );
        }

        if (status === "removing") {
            return (
                <Text size="tiny" isMuted className="setu:mt-1 setu:text-left">
                    removing...
                </Text>
            );
        }

        return (
            <Text size="tiny" isMuted className="setu:mt-1 setu:text-left">
                {formatFileSize(fileSize)}
            </Text>
        );
    };

    const isProcessing = status === "uploading" || status === "removing";

    return (
        <div className="setu:flex-1 setu:min-w-0">
            <Text
                size="tiny"
                weight="medium"
                className={`setu:truncate setu:text-left ${isProcessing ? "setu:text-muted-foreground" : ""}`}
            >
                {getFileNameWithoutExtension(fileName)}
            </Text>
            {getStatusText()}
        </div>
    );
};

const FileActions = ({ fileName, status, index, onRemove, onRetry }: FileActionsProps) => {
    // Show loading spinner when uploading or removing
    if (status === "uploading" || status === "removing") {
        return (
            <div className="setu:flex setu:items-center setu:justify-center setu:w-8 setu:h-8">
                <Spinner className="setu:text-muted-foreground" />
            </div>
        );
    }

    // Show retry button for upload errors
    if (status === "upload_error" && onRetry) {
        return (
            <Button
                variant="ghost"
                size="icon-sm"
                onClick={createClickHandler(onRetry, index)}
                aria-label={`Retry upload ${fileName}`}
            >
                <RefreshCcw className="setu:w-4 setu:h-4" />
            </Button>
        );
    }

    // Show remove button for idle or remove_error status
    if (status === "idle" || status === "remove_error") {
        return (
            <Button
                variant="ghost"
                size="icon-sm"
                onClick={createClickHandler(onRemove, index)}
                aria-label={`Remove ${fileName}`}
            >
                <Trash2 className="setu:w-4 setu:h-4 setu:text-muted-foreground" />
            </Button>
        );
    }

    return null;
};

const FileItemRow = ({ fileItem, index, onRemove, onRetry }: FileItemRowProps) => {
    const { file, status } = fileItem;
    const Icon = getFileIcon(file.name);

    return (
        <div
            key={`${file.name}-${index}`}
            className="setu:flex setu:items-start setu:justify-between setu:gap-2 setu:w-full"
        >
            <div className="setu:flex setu:items-start setu:gap-2 setu:min-w-0 setu:flex-grow">
                <div className="setu:flex-shrink-0 setu:pt-[2px]">
                    <Icon height={16} width={16} />
                </div>
                <FileInfo fileName={file.name} fileSize={file.size} status={status} />
            </div>
            <div className="setu:flex-shrink-0 setu:ml-2">
                <FileActions fileName={file.name} status={status} index={index} onRemove={onRemove} onRetry={onRetry} />
            </div>
        </div>
    );
};

const FileList = ({ files, onFileRemove, onRetryUpload }: FileListProps) => {
    const handleRemove = (index: number) => onFileRemove([index]);
    const handleRemoveAll = () => onFileRemove(files.map((_, index) => index));

    return (
        <div className="setu:flex setu:flex-col setu:gap-6 setu:w-full">
            {files?.length > 1 && (
                <div className="setu:flex setu:items-center setu:justify-between">
                    <Text size="tiny" weight="semibold">
                        {files.length} Files
                    </Text>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={createClickHandler(handleRemoveAll)}
                        className="setu:h-auto setu:px-3 setu:py-0 setu:!text-xs setu:font-medium"
                    >
                        Delete all
                    </Button>
                </div>
            )}

            <div className="setu:flex setu:flex-col setu:gap-4">
                {files.map((fileItem, index) => (
                    <FileItemRow
                        key={`${fileItem.file.name}-${index}`}
                        fileItem={fileItem}
                        index={index}
                        onRemove={handleRemove}
                        onRetry={onRetryUpload}
                    />
                ))}
            </div>
        </div>
    );
};

export default FileList;
